import { useState, useCallback, useMemo } from 'react';

import { Segment } from '../../services/analytics-services/segment';
import Global from '../../store/global-selectors';
import { ShippyEvents } from '../../tracking/tracking-constants';
import type { ShippySidebarOpenSource } from './shippy-sidebar-context';

// getState is passed in because this hook is called in App, which is above ReduxProvider.
// useSelector is not available here.
export const useShippySidebarState = (getState: () => unknown) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  const getSellerIdPayload = useCallback(() => ({ sellerId: Global.selectSellerSellerId(getState()) }), [getState]);

  const trackOpen = useCallback(
    (source: ShippySidebarOpenSource) => {
      Segment.trackEvent(ShippyEvents.SIDEBAR_OPENED, { source, ...getSellerIdPayload() });
    },
    [getSellerIdPayload],
  );

  const trackClose = useCallback(() => {
    Segment.trackEvent(ShippyEvents.SIDEBAR_CLOSED, getSellerIdPayload());
  }, [getSellerIdPayload]);

  const open = useCallback(
    (source?: ShippySidebarOpenSource) => {
      setIsOpen((prev) => {
        if (!prev) {
          trackOpen(source ?? 'shippy_button');
        }
        return true;
      });
      setHasUnreadMessages(false);
    },
    [trackOpen],
  );

  const close = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        trackClose();
      }
      return false;
    });
  }, [trackClose]);

  const toggle = useCallback(
    (source?: ShippySidebarOpenSource) => {
      setIsOpen((prev) => {
        if (!prev) {
          trackOpen(source ?? 'shippy_button');
          setHasUnreadMessages(false);
        } else {
          trackClose();
        }
        return !prev;
      });
    },
    [trackOpen, trackClose],
  );

  const contextValue = useMemo(
    () => ({
      isOpen,
      hasUnreadMessages,
      openSidebar: open,
      closeSidebar: close,
      toggleSidebar: toggle,
      setHasUnreadMessages,
    }),
    [isOpen, hasUnreadMessages, open, close, toggle],
  );

  return { isOpen, contextValue, toggle, close };
};
